import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { getHolidays, syncHolidays } from "../../../core/data/redux/milestonesSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import { all_routes } from "../../router/all_routes";

interface HolidayItem {
  id: string;
  date: string;
  day: string;
  dateNum: string;
  name: string;
  type: "NH" | "GH" | "RH";
  holidayCalID?: string;
  txtLong: string;
  rawDate: string;
}

interface MonthGroup {
  id: string;
  month: string;
  monthShort: string;
  year: number;
  holidays: HolidayItem[];
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

const parseHolidayType = (txtLong: string): "NH" | "GH" | "RH" => {
  const clean = (txtLong ?? "").toUpperCase();
  if (clean.includes("RH") || clean.includes("RESTRICTED")) return "RH";
  if (clean.includes("NH") || clean.includes("NATIONAL")) return "NH";
  if (clean.includes("GH") || clean.includes("GAZETTED")) return "GH";
  return "RH";
};

const extractName = (item: any): string => {
  const candidates = [
    item?.txtLong,
    item?.txtShort,
    item?.name,
    item?.holidayName,
    item?.title,
    item?.description,
  ];
  for (const c of candidates) {
    const s = (c ?? "").toString().trim();
    if (s.length > 0) return s;
  }
  return "Holiday";
};

const pickIcon = (name: string): string => {
  const n = name.toUpperCase();
  if (n.includes("REPUBLIC") || n.includes("INDEPENDENCE")) return "🇮🇳";
  if (n.includes("HOLI")) return "🎨";
  if (n.includes("EID") || n.includes("MILAD") || n.includes("MUHARRAM") || n.includes("BAKRID")) return "☪️";
  if (n.includes("CHRISTMAS")) return "🎄";
  if (n.includes("DIWALI") || n.includes("DEEPAVALI")) return "🪔";
  if (n.includes("DUSSEHRA") || n.includes("RAM NAVAMI")) return "🏹";
  if (n.includes("GANDHI")) return "🕊️";
  if (n.includes("GURU")) return "🙏";
  if (n.includes("BUDDHA")) return "☸️";
  if (n.includes("MAHAVIR")) return "☮️";
  if (n.includes("NEW YEAR")) return "🎆";
  return "🗓️";
};

interface PublicHolidaysProps {
  pageTitle?: string;
}

const PublicHolidays: React.FC<PublicHolidaysProps> = ({ pageTitle }) => {
  const routes = all_routes;
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { holidays, loading } = useSelector((state: any) => state.milestonesState);

  const isDMRCCalendarRoute = location.pathname.includes("dmrc-calendar");
  const title = pageTitle || (isDMRCCalendarRoute ? "DMRC Calendar" : "Public Holiday Calendar");

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(new Date().getMonth());
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [showAdditionalModal, setShowAdditionalModal] = useState<boolean>(false);
  const [highlightedDate, setHighlightedDate] = useState<string | null>(null);

  // Fetch holidays with optional user holidayCalID
  const loadHolidays = useCallback(async () => {
    try {
      let holidayCalID = "";
      try {
        const profileRes = await dispatch(getMyProfile({})).unwrap();
        const empData = profileRes?.data || profileRes?.data?.data;
        holidayCalID = empData?.employee_work_schedules?.holidayCalID || "";
      } catch (err) {
        // Continue even if profile is unavailable
      }

      dispatch(getHolidays({ year: selectedYear, holidayCalID: holidayCalID || undefined }));
    } catch (error) {
      console.error("Error loading public holidays:", error);
    }
  }, [dispatch, selectedYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Sync holidays via API
  const handleSync = async () => {
    try {
      setIsSyncing(true);
      const res = await dispatch(syncHolidays({ year: selectedYear })).unwrap();
      toast.success(res?.message || "Public holiday list synchronized successfully!");
      await loadHolidays();
    } catch (err: any) {
      toast.error(err?.message || "Failed to synchronize holiday list");
    } finally {
      setIsSyncing(false);
    }
  };

  // Resilient holiday list extraction
  const rawList: any[] = useMemo(() => {
    let list: any[] = [];
    if (Array.isArray(holidays?.data)) list = holidays.data;
    else if (Array.isArray(holidays?.data?.data)) list = holidays.data.data;
    else if (Array.isArray(holidays?.data?.data?.data)) list = holidays.data.data.data;
    else if (Array.isArray(holidays)) list = holidays;
    return list;
  }, [holidays]);

  // Group into all 12 months (matching mobile groupByMonth)
  const monthGroups: MonthGroup[] = useMemo(() => {
    const map = new Map<number, MonthGroup>();

    // Pre-populate all 12 months
    for (let m = 0; m < 12; m++) {
      map.set(m, {
        id: `${selectedYear}-${m}`,
        month: MONTH_NAMES[m],
        monthShort: MONTH_SHORT[m],
        year: selectedYear,
        holidays: [],
      });
    }

    rawList.forEach((item, idx) => {
      const rawDate = (item.date ?? "").toString();
      if (!rawDate) return;

      const datePart = rawDate.split("T")[0];
      const parts = datePart.split("-");
      if (parts.length < 3) return;

      const yr = parseInt(parts[0], 10);
      const monthIdx = parseInt(parts[1], 10) - 1; // 0-based
      const dayNum = parseInt(parts[2], 10);

      if (yr !== selectedYear || monthIdx < 0 || monthIdx > 11) return;

      const dObj = new Date(yr, monthIdx, dayNum);
      const dayName = DAY_NAMES[dObj.getDay()];
      const dateNumStr = String(dayNum).padStart(2, "0");
      const name = extractName(item);

      const holiday: HolidayItem = {
        id: String(item.id ?? idx),
        date: rawDate,
        rawDate: datePart,
        day: dayName,
        dateNum: dateNumStr,
        name,
        type: parseHolidayType(item.txtLong || name),
        holidayCalID: item.holidayCalID,
        txtLong: item.txtLong || name,
      };

      if (map.has(monthIdx)) {
        map.get(monthIdx)!.holidays.push(holiday);
      }
    });

    // Sort holidays within each month by date
    map.forEach((group) => {
      group.holidays.sort(
        (a, b) => parseInt(a.dateNum, 10) - parseInt(b.dateNum, 10)
      );
    });

    return Array.from(map.values()).sort((a, b) => {
      const aM = parseInt(a.id.split("-")[1], 10);
      const bM = parseInt(b.id.split("-")[1], 10);
      return aM - bM;
    });
  }, [rawList, selectedYear]);

  // Selected Month Group
  const currentMonthGroup = useMemo(() => {
    return monthGroups[selectedMonthIndex] || monthGroups[0];
  }, [monthGroups, selectedMonthIndex]);

  // All-year total holiday count (matching mobile allYearTotal)
  const allYearTotal = useMemo(() => {
    return monthGroups.reduce((acc, m) => acc + m.holidays.length, 0);
  }, [monthGroups]);

  // Stats for the selected month (matching mobile StatsRow)
  const monthStats = useMemo(() => {
    let nh = 0;
    let gh = 0;
    let rh = 0;

    (currentMonthGroup?.holidays || []).forEach((h) => {
      if (h.type === "NH") nh++;
      else if (h.type === "GH") gh++;
      else rh++;
    });

    return { nh, gh, rh, total: currentMonthGroup?.holidays?.length || 0 };
  }, [currentMonthGroup]);

  // Year switcher
  const handleYearChange = (dir: "prev" | "next") => {
    setSelectedYear((prev) => (dir === "next" ? prev + 1 : prev - 1));
  };

  const handleSendEmail = () => {
    const subject = encodeURIComponent("Public Holiday Calendar Update Request");
    const body = encodeURIComponent(
      `Dear HR Team,\n\nPlease review and update my public holiday calendar for the year ${selectedYear}.\n\nThank you.`
    );
    window.location.href = `mailto:hr@company.com?subject=${subject}&body=${body}`;
  };

  // Month Calendar Cells Generation
  const calendarCells = useMemo(() => {
    const firstDayOfWeek = new Date(selectedYear, selectedMonthIndex, 1).getDay(); // 0 = Sun
    const daysInMonth = new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();

    const cells: (string | null)[] = [
      ...Array(firstDayOfWeek).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, "0")),
    ];

    return { cells, firstDayOfWeek, daysInMonth };
  }, [selectedYear, selectedMonthIndex]);

  // Map dateNum -> HolidayItem[]
  const holidayMap = useMemo(() => {
    const map = new Map<string, HolidayItem[]>();
    (currentMonthGroup?.holidays || []).forEach((h) => {
      const existing = map.get(h.dateNum) ?? [];
      existing.push(h);
      map.set(h.dateNum, existing);
    });
    return map;
  }, [currentMonthGroup]);

  const today = new Date();
  const isCurrentMonthNow =
    today.getFullYear() === selectedYear && today.getMonth() === selectedMonthIndex;
  const todayNum = String(today.getDate()).padStart(2, "0");

  return (
    <div className="page-wrapper bg-light min-vh-100">
      <style>{`
        /* Standardized Header Toolbar Card */
        .pub-control-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 8px 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .pub-year-btn {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #334155;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .pub-year-btn:hover {
          background: #e2e8f0;
          color: #0f172a;
          transform: scale(1.05);
        }
        .pub-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
        }
        .pub-legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* Month Pills Scroller */
        .month-pills-scroller {
          display: flex;
          gap: 6px;
          overflow-x: auto;
          padding-bottom: 4px;
          scrollbar-width: thin;
        }
        .month-pills-scroller::-webkit-scrollbar {
          height: 3px;
        }
        .month-pills-scroller::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .pub-month-pill {
          min-width: 58px;
          padding: 6px 6px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          gap: 3px;
        }
        .pub-month-pill:hover {
          border-color: #dc3545;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        }
        .pub-month-pill.active {
          background: #dc3545;
          border-color: #dc3545;
          color: #ffffff !important;
          box-shadow: 0 3px 10px rgba(220, 53, 69, 0.25);
        }
        .pub-month-pill .pill-name {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        .pub-month-pill .pill-dots {
          display: flex;
          gap: 2px;
          height: 4px;
          align-items: center;
        }
        .pub-month-pill .pill-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #dc3545;
        }
        .pub-month-pill.active .pill-dot {
          background: #ffffff;
        }

        /* Stats Chips */
        .pub-stat-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .pub-stat-chip .chip-count {
          font-weight: 800;
          font-size: 13px;
        }

        /* Month Mini-Calendar Grid */
        .pub-calendar-card {
          background: #ffffff;
          border: 1px solid #edf2f7;
          border-radius: 12px;
          padding: 12px 14px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
        }
        .pub-cal-day-row {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin-bottom: 4px;
        }
        .pub-cal-day-label {
          text-align: center;
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          padding: 2px 0;
        }
        .pub-cal-day-label.is-weekend {
          color: #dc3545;
        }
        .pub-cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }
        .pub-cal-cell {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .pub-cal-circle {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          color: #1e293b;
          transition: all 0.2s;
        }
        .pub-cal-circle.is-weekend {
          background-color: #dc3545 !important;
          color: #ffffff !important;
          font-weight: 700;
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.25);
        }
        .pub-cal-circle.is-gh {
          background-color: #dc3545 !important;
          color: #ffffff !important;
          font-weight: 700;
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.25);
        }
        .pub-cal-circle.is-rh {
          background-color: #198754 !important;
          color: #ffffff !important;
          font-weight: 700;
          box-shadow: 0 2px 4px rgba(25, 135, 84, 0.25);
        }
        .pub-cal-circle.is-today-ring {
          outline: 2px solid #dc3545;
          outline-offset: 1px;
        }
        .pub-multi-dot-row {
          display: flex;
          gap: 2px;
          position: absolute;
          bottom: 2px;
        }
        .pub-tiny-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
        }

        /* Holiday Cards */
        .pub-holiday-card {
          background: #ffffff;
          border: 1px solid #edf2f7;
          border-radius: 12px;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
        }
        .pub-holiday-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
          border-color: #e2e8f0;
        }
        .pub-holiday-card.highlighted {
          border: 2px solid #dc3545;
          background: #fff8f8;
        }
        .pub-date-block {
          width: 44px;
          height: 44px;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          flex-shrink: 0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }
        .pub-date-block.gh-gradient {
          background: #dc3545;
        }
        .pub-date-block.rh-gradient {
          background: #198754;
        }
        .pub-date-number {
          font-size: 16px;
          font-weight: 800;
          line-height: 1;
        }
        .pub-date-day {
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 1px;
          opacity: 0.95;
        }
        .pub-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }
        .pub-type-badge.gh-badge {
          background: #fde8e8;
          color: #dc3545;
          border: 1px solid #f8b4b4;
        }
        .pub-type-badge.rh-badge {
          background: #e6f9ed;
          color: #198754;
          border: 1px solid #b7ebd0;
        }
        .pub-type-badge .dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
        }
        .pub-type-badge.gh-badge .dot {
          background: #dc3545;
        }
        .pub-type-badge.rh-badge .dot {
          background: #198754;
        }
      `}</style>

      <div className="content">
        {/* Top Header & Sync Button */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
          <div className="d-flex align-items-center">
            <Link to={routes.employeeDashboard} className="text-dark me-2">
              <i className="ti ti-arrow-left fs-18"></i>
            </Link>
            <div>
              <h5 className="fw-bold mb-0 text-dark">{title}</h5>
              <nav>
                <ol className="breadcrumb mb-0 small" style={{ fontSize: "11px" }}>
                  <li className="breadcrumb-item">
                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                  </li>
                  {isDMRCCalendarRoute ? (
                    <li className="breadcrumb-item active" aria-current="page">
                      DMRC Calendar
                    </li>
                  ) : (
                    <>
                      <li className="breadcrumb-item">
                        <Link to={routes.dmrcCalendar}>DMRC Calendar</Link>
                      </li>
                      <li className="breadcrumb-item active" aria-current="page">
                        Public Holidays
                      </li>
                    </>
                  )}
                </ol>
              </nav>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm d-flex align-items-center rounded-pill px-3 py-1 shadow-sm"
              style={{ fontSize: "12px" }}
              onClick={handleSync}
              disabled={isSyncing || loading === "pending"}
              title="Sync latest holidays with SAP"
            >
              <i className={`ti ti-refresh fs-14 me-1 ${isSyncing ? "fa-spin" : ""}`}></i>
              {isSyncing ? "Syncing..." : "Sync Holidays"}
            </button>
          </div>
        </div>

        {/* Compact Standardized Toolbar Card (replaces bright red hero banner & extra button row) */}
        <div className="pub-control-card mb-2">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            {/* Year Navigator & Annual Total */}
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="pub-year-btn"
                onClick={() => handleYearChange("prev")}
                title="Previous Year"
              >
                <i className="ti ti-chevron-left fs-14"></i>
              </button>
              <span className="fw-bold fs-16 text-dark px-1" style={{ minWidth: "46px", textAlign: "center" }}>
                {selectedYear}
              </span>
              <button
                type="button"
                className="pub-year-btn"
                onClick={() => handleYearChange("next")}
                title="Next Year"
              >
                <i className="ti ti-chevron-right fs-14"></i>
              </button>

              <span className="badge bg-light text-dark border ms-1 px-2 py-1 fw-semibold" style={{ fontSize: "12px" }}>
                <i className="ti ti-calendar me-1 text-danger"></i>
                {loading === "pending"
                  ? "Loading..."
                  : `${allYearTotal} Holidays this year`}
              </span>
            </div>

            {/* Standardized Legend */}
            <div className="d-flex align-items-center gap-3">
              <span className="pub-legend-item">
                <span className="pub-legend-dot" style={{ backgroundColor: "#dc3545" }} />
                <span>National (NH)</span>
              </span>
              <span className="pub-legend-item">
                <span className="pub-legend-dot" style={{ backgroundColor: "#dc3545" }} />
                <span>Gazetted (GH)</span>
              </span>
              <span className="pub-legend-item">
                <span className="pub-legend-dot" style={{ backgroundColor: "#198754" }} />
                <span>Restricted (RH)</span>
              </span>
            </div>

            {/* Action Buttons */}
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center rounded-pill px-3 py-1"
                onClick={() => setShowAdditionalModal(true)}
                style={{ fontWeight: 600, fontSize: "12px" }}
              >
                <i className="ti ti-calendar-event fs-14 me-1"></i> View Additional Calendar
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm d-flex align-items-center rounded-pill px-3 py-1"
                onClick={handleSendEmail}
                style={{
                  backgroundColor: "#dc3545",
                  borderColor: "#dc3545",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                <i className="ti ti-mail-forward fs-14 me-1"></i> Send Email to Update Calendar
              </button>
            </div>
          </div>
        </div>

        {/* Month Pills Navigation */}
        <div className="month-pills-scroller mb-2">
          {monthGroups.map((group, index) => {
            const isSelected = selectedMonthIndex === index;
            const hCount = group.holidays.length;

            return (
              <div
                key={group.id}
                className={`pub-month-pill ${isSelected ? "active" : ""}`}
                onClick={() => {
                  setSelectedMonthIndex(index);
                  setHighlightedDate(null);
                }}
              >
                <span className="pill-name">{group.monthShort}</span>
                <div className="pill-dots">
                  {Array.from({ length: Math.min(hCount, 3) }).map((_, i) => (
                    <span key={i} className="pill-dot" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Month Header & Compact Stats Row */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 px-1 gap-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>
              {currentMonthGroup?.month} {selectedYear}
            </h6>
            <span className="text-muted small">
              · {monthStats.total === 1
                ? "1 Holiday"
                : `${monthStats.total} Holidays`}
            </span>
          </div>

          {/* Compact Stats Chips */}
          <div className="d-flex align-items-center gap-2">
            <div className="pub-stat-chip">
              <span className="chip-count text-danger">{monthStats.nh}</span>
              <span className="text-muted">National</span>
            </div>
            <div className="pub-stat-chip">
              <span className="chip-count text-danger">{monthStats.gh}</span>
              <span className="text-muted">Gazetted</span>
            </div>
            <div className="pub-stat-chip">
              <span className="chip-count text-success">{monthStats.rh}</span>
              <span className="text-muted">Restricted</span>
            </div>
          </div>
        </div>

        {/* Main Grid: Mini-Calendar on Left & Cards on Right */}
        <div className="row g-3 mb-2">
          {/* Left Column: Month Mini-Calendar Grid */}
          <div className="col-lg-5 col-md-5">
            <div className="pub-calendar-card">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "13px" }}>
                  <i className="ti ti-calendar me-1 text-danger"></i> Monthly Schedule
                </h6>
                <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                  {calendarCells.daysInMonth} Days
                </span>
              </div>

              {/* Day-of-week header: S M T W T F S */}
              <div className="pub-cal-day-row">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                  <div
                    key={i}
                    className={`pub-cal-day-label ${i === 0 || i === 6 ? "is-weekend" : ""}`}
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells grid */}
              <div className="pub-cal-grid">
                {calendarCells.cells.map((dateNum, idx) => {
                  if (!dateNum) {
                    return <div key={`blank-${idx}`} className="pub-cal-cell" />;
                  }

                  const holidaysOnDate = holidayMap.get(dateNum) || [];
                  const primaryHoliday = holidaysOnDate[0];
                  const hasHoliday = holidaysOnDate.length > 0;
                  const isRH = primaryHoliday?.type === "RH";

                  const dayOfWeek =
                    (calendarCells.firstDayOfWeek + parseInt(dateNum, 10) - 1) % 7;
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const isToday = isCurrentMonthNow && dateNum === todayNum;
                  const isHighlighted = highlightedDate === dateNum;

                  return (
                    <div
                      key={`date-${dateNum}`}
                      className="pub-cal-cell"
                      onClick={() => {
                        if (hasHoliday) {
                          setHighlightedDate(dateNum);
                        }
                      }}
                      style={{ cursor: hasHoliday ? "pointer" : "default" }}
                    >
                      <div
                        className={`pub-cal-circle ${
                          isToday ? "is-today-ring" : ""
                        } ${
                          isWeekend
                            ? "is-weekend"
                            : hasHoliday
                            ? isRH
                              ? "is-rh"
                              : "is-gh"
                            : ""
                        } ${isHighlighted ? "shadow-sm" : ""}`}
                      >
                        {parseInt(dateNum, 10)}
                      </div>

                      {/* Multi-dots or RH indicator on weekend */}
                      {hasHoliday && (
                        <div className="pub-multi-dot-row">
                          {isWeekend && isRH ? (
                            <span
                              className="pub-tiny-dot"
                              style={{ backgroundColor: "#198754" }}
                              title="Restricted Holiday (RH)"
                            />
                          ) : holidaysOnDate.length > 1 ? (
                            holidaysOnDate.slice(1, 3).map((h, di) => (
                              <span
                                key={di}
                                className="pub-tiny-dot"
                                style={{
                                  backgroundColor: h.type === "RH" ? "#198754" : "#dc3545",
                                }}
                              />
                            ))
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Holiday Cards */}
          <div className="col-lg-7 col-md-7">
            {loading === "pending" ? (
              <div className="text-center py-4 bg-white rounded border">
                <div className="spinner-border spinner-border-sm text-danger mb-2"></div>
                <p className="text-muted small mb-0">Loading holidays...</p>
              </div>
            ) : currentMonthGroup.holidays.length > 0 ? (
              <div
                className="d-flex flex-column gap-2"
                style={{
                  maxHeight: "340px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {currentMonthGroup.holidays.map((holiday, index) => {
                  const isRH = holiday.type === "RH";
                  const icon = pickIcon(holiday.name);
                  const isFocused = highlightedDate === holiday.dateNum;

                  return (
                    <div
                      key={holiday.id || index}
                      className={`pub-holiday-card ${isFocused ? "highlighted" : ""}`}
                    >
                      {/* Left Date Block */}
                      <div
                        className={`pub-date-block ${isRH ? "rh-gradient" : "gh-gradient"}`}
                      >
                        <span className="pub-date-number">{holiday.dateNum}</span>
                        <span className="pub-date-day">{holiday.day}</span>
                      </div>

                      {/* Right Content */}
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          {/* <span style={{ fontSize: "14px" }}>{icon}</span> */}
                          <h6
                            className="fw-bold mb-0 text-dark text-truncate"
                            style={{ fontSize: "13px" }}
                            title={holiday.name}
                          >
                            {holiday.name}
                          </h6>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`pub-type-badge ${isRH ? "rh-badge" : "gh-badge"}`}
                          >
                            <span className="dot"></span>
                            {holiday.type === "NH"
                              ? "National Holiday"
                              : holiday.type === "GH"
                                ? "Gazetted Holiday"
                                : "Restricted Holiday"}
                          </span>

                          <span className="text-muted small ms-auto" style={{ fontSize: "11px" }}>
                            {convertDateToFull(holiday.rawDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 bg-white rounded border p-3">
                <span className="fs-24 mb-1 d-block">📭</span>
                <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                  No holidays scheduled for this month
                </h6>
                <p className="text-muted small mb-0" style={{ fontSize: "12px" }}>
                  Select another month to view upcoming gazetted and restricted holidays.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Holiday Calendars Alert */}
      <Modal
        open={showAdditionalModal}
        onOk={() => setShowAdditionalModal(false)}
        onCancel={() => setShowAdditionalModal(false)}
        centered
        width={420}
        okText="OK"
        okButtonProps={{
          style: {
            backgroundColor: "#dc3545",
            borderColor: "#dc3545",
            borderRadius: "6px",
            fontWeight: 600,
          },
        }}
        cancelButtonProps={{ style: { display: "none" } }}
        title={
          <div className="d-flex align-items-center gap-2 text-danger">
            <i className="ti ti-calendar-event fs-18"></i>
            <span className="fw-bold fs-16">Additional Holiday Calendars</span>
          </div>
        }
      >
        <div className="py-3">
          <p className="mb-0 text-secondary fs-14">
            View calendars for regions and countries.
          </p>
        </div>
      </Modal>
    </div>
  );
};

const convertDateToFull = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
    weekday: "short",
  });
};

export default PublicHolidays;
